import { useEffect, useRef, useState } from "react";
import { Box, BoxProps, Collapse } from "@mui/material";
import { useTranslation } from "next-i18next";
import { Typography } from "src/components-mui/base/typography/typography";

const EXPANDABLE_TEXT_EXPAND_TEST_ID = "expandable-text-expand";

export interface IExpandableTextProps extends BoxProps {
    text: string;
    noOfLines?: number;
}

export const ExpandableText = ({ text, noOfLines = 4, sx, ...boxProps }: IExpandableTextProps) => {
    const { t } = useTranslation("common");
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTextExpandable, setIsTextExpandable] = useState(false);
    const collapseRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        const collapseElement = collapseRef.current;
        const textElement = textRef.current;
        if (!collapseElement || !textElement) {
            return;
        }

        const updateIsTextExpandable = () => setIsTextExpandable(textElement.offsetHeight > collapseElement.clientHeight);
        const resizeObserver = new ResizeObserver(updateIsTextExpandable);
        resizeObserver.observe(textElement);
        resizeObserver.observe(collapseElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <Box {...boxProps} sx={[{ position: "relative" }, ...(Array.isArray(sx) ? sx : [sx])]}>
            <Collapse ref={collapseRef} in={isExpanded} collapsedSize={`${noOfLines}lh`}>
                <Typography ref={textRef} component="p" sx={{ m: 0, whiteSpace: "pre-line" }}>
                    {text}
                </Typography>
            </Collapse>
            {isTextExpandable && !isExpanded && (
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        width: "100%",
                        textAlign: "right",
                        backgroundImage: "linear-gradient(rgba(255, 255, 255, 0.01), rgba(255, 255, 255, 0.6), var(--mui-palette-common-white))",
                    }}
                >
                    <Typography
                        component="button"
                        type="button"
                        onClick={() => setIsExpanded(true)}
                        data-test-id={EXPANDABLE_TEXT_EXPAND_TEST_ID}
                        sx={{
                            px: 2,
                            border: 0,
                            cursor: "pointer",
                            color: "green.600",
                            backgroundColor: "white",
                            fontWeight: "semibold",
                            fontSize: "inherit",
                        }}
                    >
                        {t("read-more")}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
